import { useMemo, useState, useEffect, useCallback } from 'react'
import { Card, InputNumber, Button, Message, Spin, Alert } from '@arco-design/web-react'
import { useTranslation } from 'react-i18next'
import type { TopupInfo, WaffoPayMethod } from '@/api/topup'
import { parseCreemProducts, calculateEpayAmountApi, calculateStripeAmountApi } from '@/api/topup'
import PresetAmountPicker from './PresetAmountPicker'
import PaymentMethodPicker from './PaymentMethodPicker'
import CreemProductPicker from './CreemProductPicker'
import PaymentConfirmModal from './PaymentConfirmModal'
import { renderQuota, displayAmountToQuota } from '@/utils/quota'

interface OnlineTopupCardProps {
  info: TopupInfo | null
  loading?: boolean
  onPay: (params: {
    channel: string
    amount: number
    payMoney: string | null
    creemProductId?: string
    waffoPayMethodIndex?: number
  }) => Promise<void>
  payLoading?: boolean
}

function getSelectableMethods(info: TopupInfo | null) {
  if (!info?.pay_methods?.length) return []
  return info.pay_methods.filter((m) => {
    if (m.type === 'stripe') return info.enable_stripe_topup
    if (m.type === 'creem') return info.enable_creem_topup
    if (m.type === 'waffo') return info.enable_waffo_topup
    return info.enable_online_topup
  })
}

function getMinTopup(info: TopupInfo | null, channel: string): number {
  if (!info) return 1
  if (channel === 'stripe') return info.stripe_min_topup ?? info.min_topup ?? 1
  if (channel === 'waffo') return info.waffo_min_topup ?? info.min_topup ?? 1
  return info.min_topup ?? 1
}

export default function OnlineTopupCard({ info, loading, onPay, payLoading }: OnlineTopupCardProps) {
  const { t } = useTranslation()
  const methods = useMemo(() => getSelectableMethods(info), [info])
  const creemProducts = useMemo(
    () => parseCreemProducts(info?.creem_products),
    [info?.creem_products]
  )
  const waffoMethods = (info?.waffo_pay_methods || []) as WaffoPayMethod[]

  const hasOnline =
    info?.enable_online_topup ||
    info?.enable_stripe_topup ||
    info?.enable_creem_topup ||
    info?.enable_waffo_topup

  const defaultMethod = methods[0]?.type || 'alipay'
  const defaultAmount = info?.amount_options?.[0] ?? info?.min_topup ?? 10

  const [channel, setChannel] = useState(defaultMethod)
  const [amount, setAmount] = useState(defaultAmount)
  const [creemProductId, setCreemProductId] = useState<string | null>(null)
  const [waffoIndex, setWaffoIndex] = useState(0)
  const [payMoney, setPayMoney] = useState<string | null>(null)
  const [amountLoading, setAmountLoading] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  useEffect(() => {
    if (methods.length && !methods.find((m) => m.type === channel)) {
      setChannel(methods[0].type)
    }
  }, [methods, channel])

  useEffect(() => {
    if (info?.amount_options?.length) {
      setAmount(info.amount_options[0])
    } else if (info?.min_topup) {
      setAmount(info.min_topup)
    }
  }, [info])

  useEffect(() => {
    if (creemProducts.length && !creemProductId) {
      setCreemProductId(creemProducts[0].productId)
    }
  }, [creemProducts, creemProductId])

  const minTopup = getMinTopup(info, channel)
  const isCreem = channel === 'creem'

  const fetchPayMoney = useCallback(async () => {
    if (isCreem) return null
    setAmountLoading(true)
    try {
      const money =
        channel === 'stripe'
          ? await calculateStripeAmountApi(amount)
          : await calculateEpayAmountApi(amount)
      setPayMoney(money)
      return money
    } catch (err: unknown) {
      Message.error(err instanceof Error ? err.message : t('tt.topup.fetchAmountFailed'))
      return null
    } finally {
      setAmountLoading(false)
    }
  }, [amount, channel, isCreem, t])

  useEffect(() => {
    if (!hasOnline || isCreem) {
      setPayMoney(null)
      return
    }
    const timer = window.setTimeout(() => {
      if (amount >= minTopup) {
        fetchPayMoney()
      }
    }, 300)
    return () => window.clearTimeout(timer)
  }, [amount, channel, hasOnline, isCreem, minTopup, fetchPayMoney])

  const handlePrePay = async () => {
    if (isCreem) {
      if (!creemProductId) {
        Message.warning(t('tt.topup.selectCreem'))
        return
      }
      setConfirmOpen(true)
      return
    }
    if (amount < minTopup) {
      Message.warning(t('tt.topup.amountMin', { min: minTopup }))
      return
    }
    const money = payMoney ?? (await fetchPayMoney())
    if (!money) return
    setConfirmOpen(true)
  }

  const handleConfirm = async () => {
    try {
      await onPay({
        channel,
        amount,
        payMoney,
        creemProductId: isCreem ? creemProductId || undefined : undefined,
        waffoPayMethodIndex: channel === 'waffo' ? waffoIndex : undefined,
      })
      setConfirmOpen(false)
    } catch {
      // parent handles message
    }
  }

  if (loading) {
    return (
      <Card className="!rounded-2xl">
        <div className="py-12 flex justify-center">
          <Spin />
        </div>
      </Card>
    )
  }

  if (!hasOnline) {
    return (
      <Card title={t('tt.topup.onlineTitle')} className="!rounded-2xl">
        <Alert type="info" content={t('tt.topup.onlineDisabled')} />
      </Card>
    )
  }

  const selectedCreem = creemProducts.find((p) => p.productId === creemProductId)

  return (
    <>
      <Card title={t('tt.topup.onlineTitle')} className="!rounded-2xl">
        <div className="space-y-4">
          <PaymentMethodPicker methods={methods} value={channel} onChange={setChannel} />

          {isCreem ? (
            <CreemProductPicker
              products={creemProducts}
              value={creemProductId}
              onChange={setCreemProductId}
            />
          ) : (
            <>
              <PresetAmountPicker
                options={info?.amount_options || []}
                discount={info?.discount}
                value={amount}
                onChange={setAmount}
              />
              <div>
                <div className="text-[13px] text-[#374151] mb-2">{t('tt.topup.amount')}</div>
                <div className="flex flex-wrap items-center gap-3">
                  <InputNumber
                    value={amount}
                    min={minTopup}
                    onChange={(v) => setAmount(v ?? minTopup)}
                    className="w-full sm:w-[160px]"
                  />
                  {amountLoading ? (
                    <Spin size={16} />
                  ) : payMoney ? (
                    <span className="text-[13px] text-[#6B7280]">
                      {t('tt.topup.payable')}{' '}
                      <span className="text-[#DC2626] font-medium">¥{payMoney}</span>
                      {' → '}
                      {t('tt.topup.estimatedCredit')}{' '}
                      <span className="text-brand font-medium">{renderQuota(displayAmountToQuota(amount))}</span>
                    </span>
                  ) : null}
                </div>
              </div>
            </>
          )}

          {channel === 'waffo' && waffoMethods.length > 0 && (
            <div>
              <div className="text-[13px] text-[#374151] mb-2">{t('tt.topup.waffoMethod')}</div>
              <div className="flex flex-wrap gap-2">
                {waffoMethods.map((m, idx) => (
                  <button
                    key={`${m.payMethodType}-${idx}`}
                    type="button"
                    onClick={() => setWaffoIndex(idx)}
                    className={`px-3 py-1.5 rounded-lg text-[13px] border ${
                      waffoIndex === idx
                        ? 'border-brand bg-[rgba(79,70,229,0.08)] text-brand'
                        : 'border-[#E2E8F0]'
                    }`}
                  >
                    {m.name || m.payMethodName}
                  </button>
                ))}
              </div>
            </div>
          )}

          <Button type="primary" size="large" loading={payLoading} onClick={handlePrePay}>
            {t('tt.topup.payNow')}
          </Button>
        </div>
      </Card>

      <PaymentConfirmModal
        visible={confirmOpen}
        loading={payLoading || amountLoading}
        paymentMethod={channel}
        amount={amount}
        payMoney={payMoney}
        creemProductName={selectedCreem?.name}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  )
}
